import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Mail, Phone, UserRound, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { commsAPI, errMsg, notifyCommsChanged, fmtFull, STATUS_META } from '../../lib/communicationsAPI';
import { tasksAPI } from '../../lib/complianceAPI';

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>
);

const ClientCard = ({ client }) => (client ? (
  <div className="rounded-lg border border-border p-3 space-y-1.5 text-sm" data-testid="comms-client-card">
    <Link to={`/clients/${client.id}`} className="font-semibold text-foreground hover:underline flex items-center gap-2" data-testid="comms-client-card-link"><Building2 className="w-4 h-4 text-primary" />{client.display_name}</Link>
    <p className="text-[11px] text-muted-foreground font-data break-all">ID {client.id}</p>
    {client.contact_name && <p className="flex items-center gap-2 text-foreground/80"><UserRound className="w-3.5 h-3.5" />{client.contact_name}</p>}
    {client.email && <p className="flex items-center gap-2 text-foreground/80 break-all"><Mail className="w-3.5 h-3.5" />{client.email}</p>}
    {client.phone && <p className="flex items-center gap-2 text-foreground/80"><Phone className="w-3.5 h-3.5" />{client.phone}</p>}
    <p className="text-xs text-muted-foreground">Assigned accountant: <span className="text-foreground">{client.assigned_accountant_name || '—'}</span></p>
  </div>
) : null);

const Tags = ({ tags, onChange }) => {
  const [v, setV] = useState('');
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs" data-testid={`comms-tag-${t}`}>
            {t}<button type="button" onClick={() => onChange(tags.filter((x) => x !== t))} aria-label={`Remove tag ${t}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="Add tag + Enter" className="h-8 text-xs" data-testid="comms-tag-input"
        onKeyDown={(e) => { if (e.key === 'Enter' && v.trim()) { e.preventDefault(); onChange([...tags, v.trim()]); setV(''); } }} />
    </div>
  );
};

const History = ({ data }) => {
  const rows = [
    ...data.assignment_history.map((a) => ({ id: a.id, at: a.at, text: `${a.from_user_name || 'Unassigned'} → ${a.to_user_name || 'Unassigned'}`, who: a.changed_by_name, why: a.reason })),
    ...data.status_history.map((s) => ({ id: s.id, at: s.at, text: `Status: ${STATUS_META[s.to_status]?.label || s.to_status}`, who: s.changed_by_name, why: s.reason })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));
  return (
    <div className="space-y-1.5" data-testid="comms-history">
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Assignment & status history</Label>
      {rows.map((r) => (
        <p key={r.id} className="text-xs text-foreground/80">{r.text}<span className="block text-[10px] text-muted-foreground">{r.who} · {fmtFull(r.at)}{r.why ? ` · ${r.why}` : ''}</span></p>
      ))}
    </div>
  );
};

// "Details" drawer: client card, urgent, follow-up, tags, linked task/job/engagement, history.
export const DetailsPanel = ({ data, onUpdated }) => {
  const c = data.conversation;
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    if (c.client_id) tasksAPI.list({ client_id: c.client_id }).then((r) => setTasks(r.data || [])).catch(() => setTasks([]));
  }, [c.client_id]);
  const patch = async (body, ok) => {
    try { const res = await commsAPI.patch(c.id, body); onUpdated(res.data); notifyCommsChanged(); if (ok) toast.success(ok); } catch (err) { toast.error(errMsg(err, 'Update failed')); }
  };
  const linked = c.linked || {};
  const setLink = (k, v) => patch({ linked: { ...linked, [k]: v || null } }, 'Link updated');

  return (
    <div className="space-y-4 p-4" data-testid="comms-details-panel">
      <ClientCard client={data.client} />
      <div className="flex items-center justify-between"><Label>Urgent</Label><Switch checked={!!c.urgent} onCheckedChange={(v) => patch({ urgent: v }, v ? 'Marked urgent' : 'No longer urgent')} data-testid="comms-urgent-toggle" /></div>
      <Field label="Follow-up date">
        <Input type="date" className="h-9" value={c.follow_up_at ? c.follow_up_at.slice(0, 10) : ''} data-testid="comms-follow-up-input"
          onChange={(e) => patch({ follow_up_at: e.target.value || null }, e.target.value ? 'Follow-up set' : 'Follow-up cleared')} />
      </Field>
      <Field label="Tags"><Tags tags={c.tags || []} onChange={(tags) => patch({ tags })} /></Field>
      <Field label="Linked task">
        <Select value={linked.task_id || 'none'} onValueChange={(v) => setLink('task_id', v === 'none' ? null : v)}>
          <SelectTrigger className="h-9" data-testid="comms-link-task-select"><SelectValue placeholder="None" /></SelectTrigger>
          <SelectContent><SelectItem value="none">None</SelectItem>{tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Job ID"><Input className="h-9 text-xs" defaultValue={linked.job_id || ''} onBlur={(e) => e.target.value !== (linked.job_id || '') && setLink('job_id', e.target.value.trim())} data-testid="comms-link-job-input" /></Field>
        <Field label="Engagement ID"><Input className="h-9 text-xs" defaultValue={linked.engagement_id || ''} onBlur={(e) => e.target.value !== (linked.engagement_id || '') && setLink('engagement_id', e.target.value.trim())} data-testid="comms-link-engagement-input" /></Field>
      </div>
      <History data={data} />
    </div>
  );
};
